import React, { useEffect, useState } from 'react'
import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client'
import { useHistory } from 'react-router'

// PRO-130: import from the gql-only LEAF, NOT the `src/api` barrel. The barrel
// pulls ./auth (→ `src/antd`) and 22 other modules onto the entry graph; this
// leaf carries only @apollo/client + gql fragments.
import { GetActiveProjectsDetails } from 'src/api/__generated__/types'
import {
  GET_ACTIVE_PROJECTS_DETAILS,
  MUTATION_VERIFY_TOKEN,
  queryMe,
  useLogoutMutation,
} from 'src/api/auth-queries'
import { loadImpersonatorMe } from 'src/api/impersonator-me'
import { prefetchMyFeatureConfig } from 'src/api/my-feature-config'
import RouteChunkSkeleton from 'src/components/main/RouteChunkSkeleton'
import { ALL_USER_FILTER_COLLECTIONS } from 'src/hooks/useSavedFilters/api'
// PRO-133: gql-only LEAF, same rule as the auth-queries import above - the
// hook module next to it drags antd icons + @transifex onto the entry graph.
import { DASHBOARD_CONFIGS } from 'src/new-components/metabase/api'
import { setRouting, setSavedFilters, setUser } from 'src/reactive-variables'
import { routingVar } from 'src/reactive-variables/routing'
import { impersonatorTokenActiveProjectsKey } from 'src/utils/impersonator'
import {
  isKickedSessionError,
  markSignedInElsewhere,
} from 'src/utils/signedInElsewhere'

import { checkForRedirects } from './utils'
// PRO-130: gql-free leaf component (useMe reactive var + localStorage only),
// safe on the boot graph. Reconciles theme with the backend post-auth.
import { ThemeSync } from '../theme/ThemeSync'

/**
 * FE-PERF-2 FE2.2: a fired request that cannot reject unhandled
 * while VerifyToken is still deciding. The commit phase rethrows the
 * captured rejection, so error handling stays byte-compatible with
 * the old serial flow.
 */
type Settled<T> = { result: T } | { rejected: unknown }
const settle = <T,>(promise: Promise<T>): Promise<Settled<T>> =>
  promise.then(
    (result) => ({ result }),
    (rejected) => ({ rejected })
  )
const unwrap = <T,>(settled: Settled<T>): T => {
  if ('rejected' in settled) throw settled.rejected
  return settled.result
}

const Router: React.FC = ({ children }) => {
  const history = useHistory()
  const { init } = useReactiveVar(routingVar)
  const [anyOtherActionRequired, setAnyOtherActionRequired] = useState(true)
  const client = useApolloClient()
  const [checkTokenMutationQuery] = useMutation(MUTATION_VERIFY_TOKEN)
  const [logoutMutationQuery] = useLogoutMutation()

  useEffect(() => {
    // init when the app is loaded;
    // FE-PERF-2 FE2.2: the boot used to serialize three round trips
    // (VerifyToken, then GetMe + AllUserFilterCollections, then
    // GetActiveProjectsDetails) behind a bare spinner. The SAME
    // requests now leave together in one round trip (PRO-133 later
    // joined ONE more to the batch: the fire-and-forget
    // DashboardConfigs prefetch below, which commits nothing at all).
    // Nothing is COMMITTED (setUser / setSavedFilters) until
    // verifyToken has confirmed the session, so the checks and every
    // failure path are unchanged - only the waiting-in-line is gone.
    // On a refused token the parallel queries fail alongside it (they
    // carry the same JWT the backend just refused) and their results
    // are dropped unread, exactly as if they had never been sent.
    async function getUser(
      mePromise: Promise<Settled<$TSFixMe>>,
      activeProjectsPromise: Promise<
        Settled<{ data?: GetActiveProjectsDetails | null }>
      > | null,
      lastActiveProjects: string[] | undefined,
      impersonatorMePromise: Promise<void>
    ) {
      try {
        const response = unwrap(await mePromise)

        // Load impersonated active projects if they were stored
        let activeProjectsResponse
        if (activeProjectsPromise) {
          try {
            activeProjectsResponse = unwrap(await activeProjectsPromise)
          } catch (activeProjectsError) {
            // FORGE-606: same failure mode as the fallback query below - a
            // company with a large/tangled project set can fail this
            // impersonated-active-projects preload too. Don't let it block
            // setUser() from running.
            // eslint-disable-next-line no-console
            console.error('err', activeProjectsError)
            // Also clear the stored ids: below they'd otherwise look like a
            // valid active-project selection (skipping the "no active
            // projects" auto-select fallback) even though the projects they
            // point at were never actually fetched.
            // eslint-disable-next-line no-param-reassign
            lastActiveProjects = undefined
          }
        }

        if (response.data && response.data.me) {
          const userData = response.data.me
          let finalActiveProjects = activeProjectsResponse
            ? activeProjectsResponse.data?.allProjects
            : userData.activeProjects
          let finalActiveProjectsIds = lastActiveProjects?.length
            ? lastActiveProjects
            : userData.activeProjectsIds

          // Fallback: if no active projects, auto-select all available projects
          if (!finalActiveProjectsIds?.length) {
            // Try ownProjectsIds first, then company's projectSet
            const allProjectIds =
              userData.ownProjectsIds?.length > 0
                ? userData.ownProjectsIds
                : userData.company?.projectSet?.edges
                    ?.map((edge: $TSFixMe) => edge?.node?.id)
                    .filter(Boolean)

            if (allProjectIds?.length) {
              try {
                const allProjectsResponse =
                  await client.query<GetActiveProjectsDetails>({
                    query: GET_ACTIVE_PROJECTS_DETAILS,
                    variables: { ids: allProjectIds },
                  })
                finalActiveProjects = allProjectsResponse.data?.allProjects
                finalActiveProjectsIds = allProjectIds
              } catch (fallbackError) {
                // FORGE-606: a company with a large/tangled project set
                // (e.g. Navigation App/Hadeland) can fail this fallback
                // query. Falling through to the outer catch left setUser()
                // uncalled and init stuck at false forever - an
                // indefinite white/loading page. Log and keep booting
                // with no active projects instead.
                // eslint-disable-next-line no-console
                console.error('err', fallbackError)
              }
            }
          }

          // Ensure the impersonator staff check has landed so setRoles (run by
          // setUser) can grant `staff-impersonator` on this first computation.
          await impersonatorMePromise
          setUser({
            ...userData,
            activeProjects: finalActiveProjects,
            activeProjectsIds: finalActiveProjectsIds,
          })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('err', e)
      }
    }

    async function getSavedFilters(filtersPromise: Promise<Settled<$TSFixMe>>) {
      try {
        const filtersResponse = unwrap(await filtersPromise)

        if (filtersResponse.data?.allUserFilterCollections) {
          setSavedFilters(
            filtersResponse.data.allUserFilterCollections.edges.map(
              ({ node }: $TSFixMe) => node
            )
          )
        }
      } catch (e) {
        // Ignore
      }
    }

    function setUserAsNonAuthenticated() {
      setRouting({
        roles: [`non-authenticated`],
        init: true,
      })
      window.localStorage.removeItem('reactQLJWT')
    }

    async function checkTokenRefreshTokenOrLogout() {
      const currentReactQLJWTToken = window.localStorage.getItem('reactQLJWT')
      // const rememberMe = window.localStorage.getItem('rememberMe')

      if (currentReactQLJWTToken) {
        // FE2.2: everything the boot needs leaves NOW, in parallel
        // with the verify itself.
        const lastActiveProjectsRaw = localStorage.getItem(
          impersonatorTokenActiveProjectsKey
        )
        // Filter out empty strings from split result
        const lastActiveProjects = lastActiveProjectsRaw
          ?.split(',')
          .filter(Boolean)
        // `queryMe`, not a bare GET_ME: a backend that predates one of the
        // document's contract-first fields refuses the whole `me`, which the
        // getUser() catch below would turn into a permanent boot skeleton for
        // every signed-in user (the FORGE-606 failure mode). queryMe re-sends
        // once without the refused fields instead — see auth-queries.ts.
        const mePromise = settle(queryMe(client))
        // PRO-459: resolve whether the real staff behind an impersonation is
        // staff, in parallel with me. Never rejects; no-ops when not
        // impersonating.
        const impersonatorMePromise = loadImpersonatorMe(client)
        const filtersPromise = settle(
          client.query({ query: ALL_USER_FILTER_COLLECTIONS })
        )
        // PRO-133: the default post-login landing is a Metabase dashboard,
        // and its config query used to wait for the dashboards route chunk to
        // mount before it could even leave (main-shell chunk -> route chunk ->
        // DashboardConfigs -> iframe). Fire it HERE instead, on the same wave
        // as GetMe and with the same JWT, so the iframe URL is known by the
        // time the route renders.
        //
        // Deliberately fire-and-forget: unlike me/filters/activeProjects this
        // commits NOTHING - the only effect is Apollo's normal cache write,
        // which `useMeDashboardConfigs` then reads cache-first. A refused
        // token, a network failure or a user with no configs all leave the
        // cache untouched, so the hook falls back to its own fetch and its own
        // soft empty state. A prefetch must never harden an empty state into
        // an error, so its result is never inspected. `settle` keeps the
        // rejection handled while VerifyToken is still deciding.
        settle(client.query({ query: DASHBOARD_CONFIGS }))
        // PRO-459 field-level seam: warm the caller's feature-config map so
        // useFeatureConfigValue reads it cache-first (fire-and-forget).
        prefetchMyFeatureConfig(client)

        const activeProjectsPromise = lastActiveProjects?.length
          ? settle(
              client.query<GetActiveProjectsDetails>({
                query: GET_ACTIVE_PROJECTS_DETAILS,
                variables: { ids: lastActiveProjects },
              })
            )
          : null

        checkTokenMutationQuery({
          variables: {
            token: currentReactQLJWTToken,
          },
          // SBX-505: keep the refusal's GraphQL errors ON the
          // resolved result so the kicked shape stays readable.
          errorPolicy: 'all',
        })
          .then((result) => {
            const verifyToken = result?.data?.verifyToken ?? null
            if (verifyToken) {
              getUser(
                mePromise,
                activeProjectsPromise,
                lastActiveProjects,
                impersonatorMePromise
              )
              getSavedFilters(filtersPromise)
            } else {
              // SBX-505: a second login rewrites the account's
              // active token and this verify refuses with the
              // kicked shape - Apollo RESOLVES that refusal with
              // data.verifyToken null and the errors beside it.
              // Mark it via the single-session seam. (PRO-51 removed
              // the login-page notice that turned this mark into a
              // user-facing explanation, so the mark is kept but
              // currently has no consumer.)
              if (isKickedSessionError(result, currentReactQLJWTToken)) {
                markSignedInElsewhere()
              }
              setUserAsNonAuthenticated()
            }
          })
          .catch((error) => {
            // the rejected shape of the same refusal (other Apollo
            // error policies / versions)
            if (isKickedSessionError(error, currentReactQLJWTToken)) {
              markSignedInElsewhere()
            }
            setUserAsNonAuthenticated()
          })
      } else {
        setUserAsNonAuthenticated()
      }
    }

    checkTokenRefreshTokenOrLogout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkTokenMutationQuery, client, history, logoutMutationQuery])

  useEffect(() => {
    if (init && anyOtherActionRequired) {
      // check for any other action otherwise set it to false.

      if (!checkForRedirects(history)) {
        setAnyOtherActionRequired(false)
      }
    }
  }, [anyOtherActionRequired, history, init])

  if (!init || anyOtherActionRequired)
    // FE-PERF-2 FE2.2: the boot gate was the app's last bare
    // spinner-on-white (the FE-PERF-1 confession's residue). The
    // same page-frame skeleton every route chunk shows stands in
    // while the parallel boot round trip decides where we land.
    return <RouteChunkSkeleton />

  return (
    <>
      <ThemeSync />
      {children}
    </>
  )
}

export default Router
